<template>
  <div class="dialog-overlay" @click.self="$emit('close')">
    <div class="dialog">
      <div class="dialog-header">
        <h2>Contratos de {{ userName }}</h2>
        <button class="close-button" @click="$emit('close')">&times;</button>
      </div>

      <div v-if="loading" class="dialog-loading">
        <div class="spinner"></div>
        <p>Cargando contratos...</p>
      </div>

      <div v-else-if="error" class="dialog-error">
        <p>{{ error }}</p>
        <button class="btn btn-secondary" @click="loadContracts">Reintentar</button>
      </div>

      <div v-else class="dialog-body">
        <div v-if="contracts.length === 0" class="empty-state">
          No hay contratos disponibles
        </div>
        <label
          v-for="contract in contracts"
          :key="contract.id"
          class="contract-item"
        >
          <input
            type="checkbox"
            :checked="contract.assigned"
            @change="toggleContract(contract)"
          />
          <span>{{ contract.name }}</span>
        </label>
      </div>

      <div v-if="!loading && !error" class="dialog-footer">
        <button class="btn btn-secondary" @click="$emit('close')">Cancelar</button>
        <button class="btn btn-primary" :disabled="saving" @click="save">
          {{ saving ? 'Guardando...' : 'Guardar' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import usersApi, { type ContractWithAssignment } from '../api/users';

const props = defineProps<{
  userId: number;
  userName: string;
}>();

const emit = defineEmits<{
  close: [];
  saved: [];
}>();

const contracts = ref<ContractWithAssignment[]>([]);
const loading = ref(true);
const saving = ref(false);
const error = ref('');

const loadContracts = async () => {
  loading.value = true;
  error.value = '';
  try {
    const response = await usersApi.getUserContracts(props.userId);
    contracts.value = response.data.contracts;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Error al cargar contratos';
  } finally {
    loading.value = false;
  }
};

const toggleContract = (contract: ContractWithAssignment) => {
  contract.assigned = !contract.assigned;
};

const save = async () => {
  saving.value = true;
  try {
    const contractIds = contracts.value
      .filter((c) => c.assigned)
      .map((c) => c.id);
    await usersApi.updateUserContracts(props.userId, contractIds);
    emit('saved');
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Error al guardar contratos';
  } finally {
    saving.value = false;
  }
};

onMounted(loadContracts);
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog {
  background-color: #1a1a1a;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #2a2a2a;
}

.dialog-header h2 {
  color: #e0e0e0;
  font-size: 18px;
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  color: #b0b0b0;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.close-button:hover {
  color: #e0e0e0;
}

.dialog-body {
  padding: 16px 24px;
  overflow-y: auto;
  flex: 1;
}

.dialog-loading,
.dialog-error {
  padding: 40px 24px;
  text-align: center;
  color: #b0b0b0;
}

.dialog-error p {
  color: #ff6b6b;
  margin: 0 0 16px 0;
}

.empty-state {
  color: #666;
  text-align: center;
  padding: 20px;
}

.contract-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.contract-item:hover {
  background-color: #2a2a2a;
}

.contract-item input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #4a90d9;
  cursor: pointer;
}

.contract-item span {
  color: #e0e0e0;
  font-size: 14px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #2a2a2a;
}

.btn {
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-primary {
  background-color: #4a90d9;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #3a7bc8;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #2a2a2a;
  color: #b0b0b0;
  border: 1px solid #3a3a3a;
}

.btn-secondary:hover {
  background-color: #3a3a3a;
  color: #e0e0e0;
}

.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #3a3a3a;
  border-top-color: #4a90d9;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
